import type { PageableContent } from '@/types'
import api from '@/utils/api/api'
import { sortStores } from '@/utils/search/stores/sortStores'

import {
  ConfigPreviewResponse,
  MissingSkuItemDto,
  MissingSkuQueryParams,
  PositionCheckResponse,
  ProductSupplierDto,
  ProductSupplierRequest,
  StockImportConfigModel,
  StockImportConfigRequest,
  StockImportConfigShortModel,
  StockImportExecutionResponse,
  StockImportLogModel,
  StockImportMetricsModel,
  StockImportRunMode,
  StoreModel,
  StoreRequest,
  StoreStatusModel,
  UpdateStoreRequest,
  ValidationResponse,
} from '../types/stockImports.types'

let productSuppliersCache: ProductSupplierDto[] | null = null
let storesCache: StoreModel[] | null = null
let storesRequestPromise: Promise<StoreModel[]> | null = null
let storeStatusesCache: StoreStatusModel[] | null = null
const inFlightGetRequests = new Map<string, Promise<unknown>>()
const forceInFlightGetRequests = new Set<string>()

const compactParams = <T extends Record<string, unknown>>(params: T) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  )

const getRequestKey = (url: string, params: Record<string, unknown>) =>
  `${url}?${JSON.stringify(Object.entries(params).sort(([left], [right]) => left.localeCompare(right)))}`

const dedupeGet = <T>(
  key: string,
  request: () => Promise<T>,
  options?: { force?: boolean }
) => {
  const existingRequest = inFlightGetRequests.get(key) as Promise<T> | undefined
  if (existingRequest && (!options?.force || forceInFlightGetRequests.has(key)))
    return existingRequest

  const requestPromise = request().finally(() => {
    if (inFlightGetRequests.get(key) === requestPromise) {
      inFlightGetRequests.delete(key)
      forceInFlightGetRequests.delete(key)
    }
  })
  inFlightGetRequests.set(key, requestPromise)
  if (options?.force) forceInFlightGetRequests.add(key)
  return requestPromise
}

export const stockImportsApi = {
  getProductSuppliers: async (options?: { force?: boolean }) => {
    if (!options?.force && productSuppliersCache) {
      return productSuppliersCache
    }

    const response = await api.get<{ productSuppliers: ProductSupplierDto[] }>(
      '/product-suppliers'
    )
    productSuppliersCache = response.data.productSuppliers

    return productSuppliersCache
  },

  createProductSupplier: async (payload: ProductSupplierRequest) => {
    const response = await api.post<{ productSupplier: ProductSupplierDto }>(
      '/product-suppliers',
      payload
    )
    const supplier = response.data.productSupplier
    if (productSuppliersCache)
      productSuppliersCache = [...productSuppliersCache, supplier]

    return supplier
  },

  updateProductSupplier: async (
    id: string,
    payload: ProductSupplierRequest
  ) => {
    const response = await api.put<{ productSupplier: ProductSupplierDto }>(
      `/product-suppliers/${id}`,
      payload
    )
    const supplier = response.data.productSupplier
    if (productSuppliersCache) {
      productSuppliersCache = productSuppliersCache.map((item) =>
        item.id === supplier.id ? supplier : item
      )
    }
    storesCache = null

    return supplier
  },

  getStores: async (options?: { force?: boolean }) => {
    if (!options?.force && storesCache) {
      return storesCache
    }

    if (storesRequestPromise) {
      return storesRequestPromise
    }

    storesRequestPromise = api
      .get<{ stores: StoreModel[] }>('/stores')
      .then((response) => {
        storesCache = sortStores(response.data.stores)
        return storesCache
      })
      .finally(() => {
        storesRequestPromise = null
      })

    return storesRequestPromise
  },

  getStoreStatuses: async (options?: { force?: boolean }) => {
    if (!options?.force && storeStatusesCache) {
      return storeStatusesCache
    }

    const response = await api.get<{ statuses: StoreStatusModel[] }>(
      '/store-statuses'
    )
    storeStatusesCache = response.data.statuses

    return storeStatusesCache
  },

  createStore: async (payload: StoreRequest) => {
    const response = await api.post<{ store: StoreModel }>('/stores', payload, {
      _skipSanitize: true,
    })
    const store = response.data.store
    if (storesCache) storesCache = sortStores([...storesCache, store])

    return store
  },

  updateStore: async (id: string, payload: UpdateStoreRequest) => {
    const response = await api.put<{ store: StoreModel }>(
      `/stores/${id}`,
      payload,
      { _skipSanitize: true }
    )
    const store = response.data.store
    if (storesCache) {
      storesCache = sortStores(
        storesCache.map((item) => (item.id === store.id ? store : item))
      )
    }

    return store
  },

  getConfigs: async (productSupplierId?: string | null) => {
    const response = await api.get<{ configs: StockImportConfigShortModel[] }>(
      '/stock-imports/configs',
      {
        params: compactParams({ productSupplierId }),
      }
    )
    return response.data.configs
  },

  getConfig: async (id: string) => {
    const response = await api.get<{ config: StockImportConfigModel }>(
      `/stock-imports/configs/${id}`
    )
    return response.data.config
  },

  createConfig: async (payload: StockImportConfigRequest) => {
    const response = await api.post<{ config: StockImportConfigModel }>(
      '/stock-imports/configs',
      payload
    )
    return response.data.config
  },

  updateConfig: async (id: string, payload: StockImportConfigRequest) => {
    const response = await api.put<{ config: StockImportConfigModel }>(
      `/stock-imports/configs/${id}`,
      payload
    )
    return response.data.config
  },

  archiveConfig: async (id: string, isArchived: boolean) => {
    const response = await api.patch<{ config: StockImportConfigModel }>(
      `/stock-imports/configs/${id}/archive`,
      { isArchived }
    )
    return response.data.config
  },

  resetChecks: async (id: string) => {
    const response = await api.post<{ config: StockImportConfigModel }>(
      `/stock-imports/configs/${id}/actions/reset-checks`
    )
    return response.data.config
  },

  validateUrl: async (url: string) => {
    const response = await api.post<ValidationResponse>(
      '/stock-imports/validate-url',
      { url }
    )
    return response.data
  },

  validateEmail: async (payload: {
    imapHost: string | null
    imapPort: string | null
    imapLogin: string
    imapPasswordRaw: string
    useSsl: boolean
    configId?: string | null
  }) => {
    const response = await api.post<ValidationResponse>(
      '/stock-imports/validate-email',
      payload
    )
    return response.data
  },

  validateSystemEmail: async (payload: {
    expectedSender: string
    subjectMask: string
    fileMask?: string
  }) => {
    const response = await api.post<ValidationResponse>(
      '/stock-imports/validate-system-email',
      payload
    )
    return response.data
  },

  previewConfig: async (
    payload: StockImportConfigRequest,
    withPrices = false
  ) => {
    const response = await api.post<ConfigPreviewResponse>(
      '/stock-imports/preview',
      payload,
      withPrices ? { params: { withPrices } } : undefined
    )
    return response.data
  },

  runImport: async (configId: string, mode: StockImportRunMode) => {
    const response = await api.post<StockImportExecutionResponse>(
      `/stock-imports/run/${configId}`,
      undefined,
      {
        params: { isTrial: mode === 'trial' },
      }
    )
    return response.data
  },

  checkPosition: async (configId: string, article: string) => {
    const response = await api.get<PositionCheckResponse[]>(
      `/stock-imports/configs/${configId}/position-check`,
      {
        params: { article },
      }
    )
    return response.data
  },

  getLogs: async (
    params: Record<string, string | number | null | undefined>,
    options?: { force?: boolean }
  ) => {
    const requestParams = compactParams(params)
    const request = () =>
      api.get<{ logs: PageableContent<StockImportLogModel> }>(
        '/stock-imports/logs',
        { params: requestParams }
      )
    const response = await dedupeGet(
      getRequestKey('/stock-imports/logs', requestParams),
      request,
      options
    )
    return response.data.logs
  },

  getMetrics: async (
    params: Record<string, string | number | null | undefined>,
    options?: { force?: boolean }
  ) => {
    const requestParams = compactParams(params)
    const request = () =>
      api.get<StockImportMetricsModel>('/stock-imports/logs/metrics', {
        params: requestParams,
      })
    const response = await dedupeGet(
      getRequestKey('/stock-imports/logs/metrics', requestParams),
      request,
      options
    )
    return response.data
  },

  getMissingSku: async (
    logId: string,
    params: MissingSkuQueryParams & { page: number; pageSize: number },
    options?: { signal?: AbortSignal }
  ) => {
    const response = await api.get<PageableContent<MissingSkuItemDto>>(
      `/stock-imports/logs/${logId}/missing-sku`,
      { params, signal: options?.signal }
    )
    return response.data
  },

  getAllMissingSku: async (logId: string, params: MissingSkuQueryParams) => {
    const pageSize = 2000
    const items: MissingSkuItemDto[] = []
    let page = 0

    while (true) {
      const response = await stockImportsApi.getMissingSku(logId, {
        ...params,
        page,
        pageSize,
      })
      items.push(...response.content)
      if (
        response.content.length === 0 ||
        items.length >= response.totalElements
      )
        break
      page += 1
    }

    return items
  },

  clearCaches: () => {
    productSuppliersCache = null
    storesCache = null
    storeStatusesCache = null
    inFlightGetRequests.clear()
    forceInFlightGetRequests.clear()
  },
}
